import React, { useState, useEffect } from "react";
import { Button as ReactstrapButton } from "reactstrap";
import { Button, Tag, Space, Badge, message } from "antd";
import type { TableColumnsType } from "antd";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkRequestAside from "./workRequestAside";
import {
  WorkRequestData,
  WorkRequestStatus,
  WorkRequestFilters,
} from "./types";
import workRequestApis from "../../api/WorkOrderManagement/WorkRequestApis";

const WorkRequestList: React.FC = () => {
  const [sm, setSm] = useState(false);
  const [workRequests, setWorkRequests] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<WorkRequestFilters>({});
  const navigate = useNavigate();
  const location = useLocation();
  const [refreshToggle, setRefreshToggle] = useState(false);

  const getStatusColor = (status: WorkRequestStatus): string => {
    const statusColors = {
      Draft: "default",
      "Pending Approval": "processing",
      Approved: "success",
      Rejected: "error",
      "In Progress": "warning",
      Completed: "success",
      Submitted: "processing",
    };
    return statusColors[status] || "default";
  };

  const getRequestForColor = (requestFor: string): string => {
    const colors: { [key: string]: string } = {
      "Construction Work": "blue",
      Service: "green",
      Maintenance: "orange",
      Others: "purple",
    };
    return colors[requestFor] || "default";
  };

  const handleEdit = (record: any) => {
    // Read flowType from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
    // Navigate to edit mode
    navigate(
      `/work-request-creation?edit=${record.workRequestId}${flowTypeQuery}`
    );
  };

  const handleSubmitForApproval = async (record: any) => {
    Swal.fire({
      title: "Submit for Approval",
      text: "Are you sure you want to submit this Work Request?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, submit it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Create FormData with workBoqId and status
          const formData = {
            workRequestId: record.workRequestId,
            status: "Submitted",
          };

          // Call the update API
          const response: any = await workRequestApis.updateWorkRequest(
            formData
          );

          if (response && response.status) {
            message.success("Work Request submitted successfully!");
            Swal.fire({
              icon: "success",
              title: "Submitted!",
              text:
                response?.data?.message ||
                "Work Request has been submitted for approval.",
              showConfirmButton: true,
              confirmButtonText: "OK",
            }).then(() => {
              // Trigger refresh by toggling the refresh state
              setRefreshToggle(!refreshToggle);
            });
          } else {
            throw new Error(response?.message || "Failed to submit BOQ");
          }
        } catch (error: any) {
          console.error("Error submitting Work Request:", error);
          message.error("Failed to submit Work Request. Please try again.");
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message || "Failed to submit BOQ. Please try again.",
          });
        }
        // const updated = updateWorkRequest(record.id, {
        //   status: WorkRequestStatus.PendingApproval,
        // });
        // if (updated) {
        //   Swal.fire(
        //     "Submitted!",
        //     "Work Request has been submitted for approval.",
        //     "success"
        //   );
        //   loadWorkRequests(); // Refresh the list
        // } else {
        //   Swal.fire("Error!", "Failed to submit work request.", "error");
        // }
      }
    });
  };

  const handleReject = (record: any) => {
    // Swal.fire({
    //   title: "Reject Work Request",
    //   input: "textarea",
    //   inputLabel: "Rejection Reason (Required)",
    //   inputPlaceholder: "Enter reason for rejection...",
    //   inputValidator: (value) => {
    //     if (!value) {
    //       return "Rejection reason is required!";
    //     }
    //   },
    //   showCancelButton: true,
    //   confirmButtonText: "Reject",
    //   confirmButtonColor: "#dc3545",
    //   cancelButtonColor: "#6c757d",
    // }).then((result) => {
    //   if (result.isConfirmed) {
    //     const updated = updateWorkRequest(record.id, {
    //       status: WorkRequestStatus.Rejected,
    //       rejectionComments: result.value
    //     });
    //     if (updated) {
    //       Swal.fire("Rejected!", "Work Request has been rejected.", "success");
    //       loadWorkRequests();
    //     }
    //   }
    // });
  };

  const columns: TableColumnsType<WorkRequestData> = [
    {
      title: "WR #",
      dataIndex: "workRequestSequenceId",
      key: "workRequestSequenceId",
      width: 250,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Work Request Title",
      dataIndex: "workRequestTitle",
      key: "workRequestTitle",
      width: 280,
      render: (text, record: any) => {
        const searchParams = new URLSearchParams(location.search);
        const flowType = searchParams.get("flowType");
        const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
        return (
          <div>
            <Link
              to={`/work-request-view/${record.workRequestId}${flowTypeQuery}`}
              className="fw-bold text-primary"
            >
              {text}
            </Link>
            <div className="text-muted small mt-1">
              {record.siteName}
              {record.projectName && ` • ${record.projectName}`}
            </div>
          </div>
        );
      },
    },
    {
      title: "Request For",
      dataIndex: "requestFor",
      key: "requestFor",
      width: 140,
      render: (text) => <Tag color={getRequestForColor(text)}>{text}</Tag>,
      // filters: [
      //   { text: "Construction Work", value: "Construction Work" },
      //   { text: "Service", value: "Service" },
      //   { text: "Maintenance", value: "Maintenance" },
      //   { text: "Others", value: "Others" },
      // ],
      // onFilter: (value, record) => record.requestFor === value,
    },
    {
      title: "Work Lines",
      dataIndex: "workRequestLinesCount",
      key: "workRequestLinesCount",
      width: 100,
      align: "center",
      render: (text) => <Badge count={text} color="#1890ff" />,
      // sorter: (a, b) => a.totalLines - b.totalLines,
    },
    {
      title: "Planned Dates",
      key: "plannedDates",
      width: 160,
      render: (_, record) => (
        <div>
          <div className="small">
            <strong>Start:</strong>{" "}
            {dayjs(record.plannedStartDate).format("DD-MMM-YYYY")}
          </div>
          <div className="small">
            <strong>End:</strong>{" "}
            {dayjs(record.plannedEndDate).format("DD-MMM-YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      // filters: [
      //   { text: "Draft", value: "Draft" },
      //   { text: "Pending Approval", value: "Pending Approval" },
      //   { text: "Approved", value: "Approved" },
      //   { text: "Rejected", value: "Rejected" },
      //   { text: "In Progress", value: "In Progress" },
      //   { text: "Completed", value: "Completed" },
      // ],
      // onFilter: (value, record) => record.status === value,
    },
    {
      title: "Approval Required",
      dataIndex: "approvalRequired",
      key: "approvalRequired",
      width: 180,
      align: "center",
      render: (required) => (
        <Tag color={required ? "orange" : "green"}>
          {required ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (text, record: any) => (
        <div>
          <div className="small">{dayjs(text).format("DD-MMM-YYYY")}</div>
          <div className="text-muted small">by {record.createdByName}</div>
        </div>
      ),
      // sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space wrap>
          {record.status === "Draft" && (
            <>
              <Button
                type="link"
                size="small"
                icon={<Icon name="edit" />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>

              <Button
                type="link"
                size="small"
                icon={<Icon name="send" />}
                onClick={() => handleSubmitForApproval(record)}
              >
                Submit
              </Button>
            </>
          )}

          {/* {(record.status === "Draft" || record.status === "Rejected") && (
            <Button
              type="link"
              size="small"
              danger
              icon={<Icon name="trash" />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )} */}
        </Space>
      ),
    },
  ];

  const handleFiltersChange = (newFilters: WorkRequestFilters) => {
    setFilters(newFilters);
  };

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-request-list"
      title="Work Request List"
      columns={columns}
      refresh={refreshToggle}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkRequestAside
            updateSm={setSm}
            sm={sm}
            onFiltersChange={handleFiltersChange}
            filters={filters}
          />
        </div>
      }
      // topRightJSX={
      //   <Link to="/work-request-creation">
      //     <ReactstrapButton color="primary">
      //       <Icon name="plus" />
      //       Create Work Request
      //     </ReactstrapButton>
      //   </Link>
      // }
      extra={{
        tableScrollX: 1400,
        showSearch: true,
        searchPlaceholder: "Search by WR#, Title, or Creator...",
        onSearch: (value: any) => {
          handleFiltersChange({ ...filters, searchText: value });
        },
      }}
    />
  );
};

export default WorkRequestList;
